"use client";

import React from "react";
import { Flame, ShieldCheck, Zap, ArrowRight, Gauge, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const keyPoints = [{
  id: "atomizacao",
  title: "Atomização homogénea",
  desc: "Gotículas micrométricas que garantem combustão completa sem resíduos.",
  icon: Flame
}, {
  id: "eficiencia",
  title: "Eficiência térmica",
  desc: "Maior recuperação de calor e redução de emissões indesejadas de SO3.",
  icon: Zap
}, {
  id: "confiabilidade",
  title: "Fiabilidade contínua",
  desc: "Lanças e bicos especiais resistentes à corrosão e altas temperaturas.",
  icon: ShieldCheck
}];
export default function Combustion() {
  return <section id="sulfurcombustionhero" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-20 md:py-28 lg:py-32">
      {/* Subtle industrial grid and ambient glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top_right,hsl(215_90%_45%)_0%,transparent_70%)]" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-secondary/20 blur-3xl" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          
          {/* Main Hero Column */}
          <div className="lg:col-span-7 flex flex-col items-start gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="bg-secondary text-secondary-foreground uppercase tracking-wider text-xs px-3 py-1 font-semibold">
                Engenharia de Processo
              </Badge>
              <span className="text-xs text-primary-foreground/75 font-medium tracking-wide">
                Spraying Systems Co. Portugal
              </span>
            </div>

            <h1 className="font-default text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl lg:text-6xl text-primary-foreground leading-tight">
              Queima de sulfúrico com máxima precisão térmica
            </h1>

            <p className="max-w-2xl text-base sm:text-lg text-primary-foreground/80 leading-relaxed font-sans">
              Projetamos sistemas e lanças de pulverização de alta tecnologia para fornos de combustão de enxofre líquido, assegurando queima estável, controlo rigoroso de gotas e redução de custos operacionais.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md font-medium px-6 py-3">
                <Link to="/ContactsPage" className="inline-flex items-center gap-2">
                  <span>Consulte-nos</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground bg-primary/20 hover:bg-primary/40 font-medium px-6 py-3">
                <Link to="/TechnicalSolutionsPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" />
                  <span>Soluções Técnicas</span>
                </Link>
              </Button>
            </div>

            {/* Technical Trust Strip */}
            <div className="mt-6 pt-6 border-t border-primary-foreground/15 w-full flex flex-wrap items-center gap-6 sm:gap-10 text-xs sm:text-sm text-primary-foreground/75">
              <div className="flex items-center gap-2">
                <Gauge className="size-4 text-secondary" />
                <span>Simulação CFD avançada</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-secondary" />
                <span>Ligas especiais anti-corrosão</span>
              </div>
            </div>
          </div>

          {/* Right Card / Technical Highlights */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-primary-foreground/15 bg-primary-foreground/5 p-6 sm:p-8 backdrop-blur-xs shadow-xl space-y-6">
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-widest text-secondary font-semibold">
                  Desempenho Comprovado
                </p>
                <h2 className="text-xl sm:text-2xl font-bold text-primary-foreground font-default">
                  Tecnologia para fornos industriais
                </h2>
              </div>

              <div className="space-y-4">
                {keyPoints.map((point, i) => {
                const Icon = point.icon;
                return <div key={point.id} data-index={i} className="group rounded-xl border border-primary-foreground/10 bg-primary/40 p-4 transition-[background-color,border-color] duration-200 hover:bg-primary/60 hover:border-secondary/50">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-secondary/20 text-secondary shrink-0 mt-0.5">
                          <Icon className="size-5" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-primary-foreground">
                            {point.title}
                          </h3>
                          <p className="text-xs text-primary-foreground/75 mt-1 leading-relaxed">
                            {point.desc}
                          </p>
                        </div>
                      </div>
                    </div>;
              })}
              </div>

              <div className="pt-2">
                <Link to="/CatalogsPage" className="inline-flex items-center gap-1.5 text-xs text-secondary hover:underline font-medium">
                  <span>Consulte o catálogo de bicos e lanças para enxofre</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}